import React from 'react'
import type { LeagueTemplate, TemplatinessStats } from 'api/league'
import { formatDecimal } from 'utils/numbers'
import TemplatePitchSurface from 'components/structure/TemplatePitchSurface'
import { TEMPLATE_PITCH_POS_ORDER } from 'components/structure/templatePitchLayout'

interface Props {
  template: LeagueTemplate
  templatiness: TemplatinessStats | null
  totalTeams: number
  shirtImages: Record<number, string>
  gkShirtImages: Record<number, string>
  isDarkMode?: boolean
  /** Tight layout inside export pitch column — badges outside pitch */
  embedded?: boolean
}

const ExportTemplatePitch: React.FC<Props> = ({
  template,
  templatiness,
  totalTeams,
  shirtImages,
  gkShirtImages,
  isDarkMode = false,
  embedded = false,
}) => {
  const textColor = isDarkMode ? '#fff' : '#1a1a1a'
  const pillBg = isDarkMode ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.92)'
  const pillBorder = isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.1)'

  const byType: Record<number, typeof template.players> = { 1: [], 2: [], 3: [], 4: [] }
  for (const row of template.players) {
    const et = Number(row.player?.element_type ?? 0)
    if (et >= 1 && et <= 4) byType[et].push(row)
  }

  const jacLeaguePct = templatiness != null ? templatiness.xi_jaccard_avg_league * 100 : null
  const showSimilarity = jacLeaguePct != null

  const statPill = (cnt: number, pct: number) => (
    <div
      style={{
        marginTop: 4,
        padding: '3px 8px',
        borderRadius: 999,
        backgroundColor: pillBg,
        border: `1px solid ${pillBorder}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontFamily: 'PremierLeagueBold, Arial, sans-serif', fontSize: '0.62rem' }}>{cnt}</span>
      <span style={{ width: 1, height: '0.65em', backgroundColor: 'rgba(0,0,0,0.18)' }} />
      <span style={{ fontFamily: 'PremierLeagueRegular, Arial, sans-serif', fontSize: '0.62rem', opacity: 0.78 }}>
        {formatDecimal(pct)}%
      </span>
    </div>
  )

  const playerCell = (row: (typeof template.players)[0], rowCount: number) => {
    const tid = row.player?.team ?? 0
    const isGk = Number(row.player?.element_type) === 1
    const shirt = isGk ? gkShirtImages[tid] ?? shirtImages[tid] : shirtImages[tid]
    const cnt = typeof row.starts_league_count === 'number' ? row.starts_league_count : 0
    const pct =
      typeof row.starts_league_pct === 'number'
        ? row.starts_league_pct
        : totalTeams > 0
          ? (cnt / totalTeams) * 100
          : 0

    return (
      <div
        key={row.player?.id ?? tid}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 58,
          maxWidth: 82,
          flex: rowCount <= 3 ? '0 1 auto' : '1 1 0',
        }}
      >
        {shirt && (
          <img
            src={shirt}
            alt=""
            style={{ width: 30, height: 38, objectFit: 'contain', display: 'block', marginBottom: 3 }}
          />
        )}
        <span
          style={{
            fontFamily: 'PremierLeagueBold, Arial, sans-serif',
            fontSize: '0.68rem',
            color: '#fff',
            textShadow: '0 0 1px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.55)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {row.player?.web_name ?? '—'}
        </span>
        {statPill(cnt, pct)}
      </div>
    )
  }

  const rowOfCells = (rows: typeof template.players) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        gap: 8,
        width: '100%',
        padding: '2px 0',
      }}
    >
      {rows.map((r) => playerCell(r, rows.length))}
    </div>
  )

  const overlayBadgeBg = isDarkMode ? 'rgba(0,0,0,0.72)' : 'rgba(255,255,255,0.94)'
  const overlayBadgeBorder = isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'

  const similarityBadge = (pct: number) => (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: embedded ? '3px 7px' : '4px 9px',
        borderRadius: 6,
        backgroundColor: overlayBadgeBg,
        border: `1px solid ${overlayBadgeBorder}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
        minWidth: embedded ? 58 : 72,
      }}
    >
      <span
        style={{
          fontFamily: 'PremierLeagueBold, Arial, sans-serif',
          fontSize: embedded ? '0.38rem' : '0.42rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          opacity: 0.65,
          textAlign: 'center',
          color: isDarkMode ? '#fff' : textColor,
        }}
      >
        Similarity
      </span>
      <span
        style={{
          fontFamily: 'PremierLeagueBold, Arial, sans-serif',
          fontSize: embedded ? '0.68rem' : '0.78rem',
          color: isDarkMode ? '#fff' : textColor,
        }}
      >
        {formatDecimal(pct)}%
      </span>
    </div>
  )

  return (
    <div style={{ width: '100%' }}>
      <h3
        style={{
          margin: '0 0 8px 0',
          fontSize: '14px',
          fontFamily: 'PremierLeagueBold, Arial, sans-serif',
          color: textColor,
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        League Template XI
      </h3>

      <TemplatePitchSurface borderRadius={embedded ? 1.5 : 2}>
        {showSimilarity && (
          <div
            style={{
              position: 'absolute',
              top: embedded ? '8%' : '10%',
              right: embedded ? '7%' : '9%',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            {similarityBadge(jacLeaguePct!)}
          </div>
        )}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            width: '100%',
            padding: '16px 20px 14px',
            boxSizing: 'border-box',
          }}
        >
          {TEMPLATE_PITCH_POS_ORDER.map((et) => {
            const rows = byType[et]
            if (!rows.length) return null
            return <React.Fragment key={et}>{rowOfCells(rows)}</React.Fragment>
          })}
        </div>
      </TemplatePitchSurface>
    </div>
  )
}

export default ExportTemplatePitch
